import { useEffect, useState } from "react";
import http from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { STATUS_KEYS, STATUS_RU } from "@/lib/statuses";
import { computeSla, slaBadgeClass } from "@/lib/sla";
import { fmt } from "./AdminKit";
import AdminRepairPhotos from "./AdminRepairPhotos";
import AdminBillingSection from "./AdminBillingSection";
import SmsButton from "./SmsButton";
import { Link } from "react-router-dom";
import { printQr } from "@/lib/qr";

const priorities = ["normal", "hoy", "akutt"];
const priorityLabel = { akutt: "Акутт", hoy: "Высокий", normal: "Обычный" };
const cleanlinessOpts = ["", "чистая", "нормальная", "грязная", "другое"];
const respLabel = { approved: "Одобрено", declined: "Отклонено", more_info: "Запрошено уточнение", pending: "Ожидает" };

export const ACCESSORIES = [
  ["power_cable", "Кабель питания"], ["portafilter", "Портафильтр (холдер)"],
  ["filter_baskets", "Фильтр-корзины"], ["milk_container", "Контейнер для молока"],
  ["water_tank", "Резервуар для воды"], ["drip_tray", "Поддон"],
  ["hoses", "Шланги"], ["keys", "Ключи"], ["external_parts", "Внешние части"],
];

const EMPTY = {
  customer_name: "", email: "", phone: "", machine_brand: "", machine_model: "", machine_type: "",
  serial_number: "", machine_location: "", problem_description: "", when_occurs: "", symptoms: "",
  previous_service: "", status: "mottatt", priority: "normal", is_business: false, received_by: "",
  accessories: {}, accessories_notes: "",
  intake_condition: { visible_damage: false, condition_notes: "", missing_parts: "", cleanliness: "", observations: "" },
  diagnosis: { text: "", findings: "", recommended_repair: "", parts_required: "", labour_estimate: "" },
  proposal: { diagnosis_summary: "", recommended_work: "", required_parts: "", labour_estimate: "", estimated_price: "", price_range: "", vat: "", conditions: "" },
  estimated_cost: "", internal_notes: "", handover_assist: "",
};

const inpCls = "mt-1 bg-slate-700 border-slate-600 text-white";

function Section({ title, children }) {
  return (
    <div className="border border-slate-700 rounded-lg">
      <div className="px-3 py-2 border-b border-slate-700 text-sm font-semibold text-slate-200 bg-slate-900/40">{title}</div>
      <div className="p-3 space-y-3">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return <div><label className="text-xs text-slate-400">{label}</label>{children}</div>;
}

export default function AdminRepairCard({ open, onOpenChange, orderId, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [order, setOrder] = useState(null);
  const [extras, setExtras] = useState({ timeline: [], messages: [], approvals: [], customer: null });
  const [loading, setLoading] = useState(false);
  const [showReady, setShowReady] = useState(false);
  const [readyForm, setReadyForm] = useState({ work_type: "reparasjon", work_summary: "", final_amount: "" });
  const [busy, setBusy] = useState(false);
  const [notifyCustomer, setNotifyCustomer] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!orderId) { setForm(EMPTY); setOrder(null); setExtras({ timeline: [], messages: [], approvals: [], customer: null }); return; }
    setLoading(true);
    http.get(`/v1/admin/repair-orders/${orderId}/card`).then((r) => {
      const o = r.data.order || {};
      setOrder(o);
      setExtras({ timeline: r.data.timeline || [], messages: r.data.messages || [], approvals: r.data.approvals || [], customer: r.data.customer || null, machine: r.data.machine || null, machine_history: r.data.machine_history || [] });
      setForm({
        ...EMPTY, ...o,
        phone: o.phone || r.data.customer?.phone || "",
        accessories: { ...EMPTY.accessories, ...(o.accessories || {}) },
        accessories_notes: o.accessories_notes || "",
        intake_condition: { ...EMPTY.intake_condition, ...(o.intake_condition || {}) },
        diagnosis: { ...EMPTY.diagnosis, ...(o.diagnosis || {}) },
        proposal: { ...EMPTY.proposal, ...(o.proposal || {}) },
        is_business: !!o.is_business,
      });
    }).catch(() => toast.error("Ошибка загрузки карточки")).finally(() => setLoading(false));
  }, [open, orderId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (grp, k, v) => setForm((f) => ({ ...f, [grp]: { ...f[grp], [k]: v } }));

  const save = async () => {
    if (!form.customer_name.trim() || !form.machine_brand.trim() || !form.problem_description.trim()) {
      toast.error("Заполните имя клиента, марку и описание проблемы"); return;
    }
    try {
      if (orderId) await http.put(`/v1/admin/repair-orders/${orderId}`, { ...form, notify_customer: notifyCustomer });
      else await http.post("/v1/admin/repair-orders", form);
      toast.success(notifyCustomer ? "Сохранено. Клиент уведомлён на его языке." : "Карточка сохранена");
      setNotifyCustomer(false); onOpenChange(false); onSaved && onSaved();
    } catch { toast.error("Ошибка сохранения"); }
  };

  const latestApproval = extras.approvals[0];

  const cust = extras.customer || {};
  const accList = (order?.accessories && typeof order.accessories === "object")
    ? Object.entries(order.accessories).filter(([, v]) => v).map(([k]) => k) : [];
  const fmtDate = (d) => { try { return d ? new Date(d).toLocaleString("nb-NO") : "-"; } catch { return d || "-"; } };
  const o = order || {};
  const summaryRows = order ? [
    ["Jobbkode", o.order_number || "-"],
    ["Mottatt", fmtDate(o.received_at || o.created_at)],
    ["Status", (STATUS_RU[o.status] || o.status || "-")],
    ["Kunde", o.customer_name || "-"],
    ["Telefon", cust.phone || o.phone || "-"],
    ["E-post", o.email || cust.email || "-"],
    ["Adresse", [o.customer_address, [o.postal_code, o.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "-"],
    ["Maskin", [o.machine_type, o.machine_brand, o.machine_model].filter(Boolean).join(" ") || "-"],
    ["Serienummer", o.serial_number || "-"],
    ["Levert komplett", o.delivered_complete ? "Ja" : "Ikke bekreftet"],
    ["Tilbehør", accList.length ? accList.join(", ") : "—"],
    ["Synlige skader", (o.intake_condition && (o.intake_condition.visible_damage || o.intake_condition.condition_notes)) || "Ingen registrert"],
    ["Feilkode", o.fault_code || "-"],
    ["Oppgitt problem", o.problem_description || "-"],
    ["Symptomer", o.symptoms || "-"],
    ["Valgt pakke", o.selected_package ? ("Pakke " + o.selected_package) : "-"],
    ["Ønsker prisoverslag", o.wants_price_estimate === true ? "Ja" : (o.wants_price_estimate === false ? "Nei" : "-")],
    ["Vilkår akseptert", o.terms_accepted ? ("Ja (v" + (o.terms_version || "-") + ", " + fmtDate(o.terms_accepted_at) + ")") : "Nei"],
  ] : [];

  const printReceipt = () => {
    const rowsHtml = summaryRows
      .filter(([k]) => k !== "Status")
      .map(([k, v]) => `<tr><th>${k}</th><td>${String(v).replace(/</g, "&lt;")}</td></tr>`).join("");
    const html = `<!doctype html><html lang="no"><head><meta charset="utf-8"><title>Innleveringskvittering ${o.order_number || ""}</title>
      <style>body{font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;max-width:720px;margin:24px auto;padding:0 16px}
      h1{font-size:20px;margin:0 0 2px}.sub{color:#555;font-size:12px;margin:0 0 16px}
      .code{display:inline-block;background:#3D2314;color:#fff;padding:6px 12px;border-radius:6px;font-size:18px;font-weight:bold;letter-spacing:1px}
      table{width:100%;border-collapse:collapse;margin-top:16px}th,td{text-align:left;vertical-align:top;padding:6px 8px;border-bottom:1px solid #eee;font-size:13px}
      th{width:38%;color:#555;font-weight:600}.note{margin-top:18px;padding:10px 12px;background:#FFF8F0;border:1px solid #e6d3b3;border-radius:6px;font-size:12px;color:#5a3520}
      .foot{margin-top:24px;font-size:11px;color:#777}.sign{margin-top:28px;display:flex;justify-content:space-between;font-size:12px}
      @media print{button{display:none}}</style></head><body>
      <h1>Bergen Kaffemaskin- og Teknikk Service</h1>
      <p class="sub">Birkeveien 2, 5093 Bergen &middot; Tlf 90 23 22 06 &middot; info@bergenkaffeogteknikk.no</p>
      <div><span class="code">${o.order_number || ""}</span></div>
      <h2 style="font-size:15px;margin:18px 0 0">Innleveringskvittering</h2>
      <table>${rowsHtml}</table>
      <div class="note"><strong>Merk:</strong> Denne kvitteringen bekrefter hva som er fysisk levert inn. Den innebærer ikke at oppgitt feil er teknisk kontrollert eller diagnostisert. Endelig pris bekreftes etter diagnostikk, og deler kommer i tillegg. Kunden har akseptert vilkår og garantibetingelser ved innsending.</div>
      <div class="sign"><span>Sted/dato: _______________</span><span>Signatur/stempel: _______________</span></div>
      <p class="foot">Ta med jobbkoden ved henting. Følg status p&aring; nettsiden under &laquo;Sporing&raquo;.</p>
      <button onclick="window.print()">Skriv ut</button></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Tillat popup for å skrive ut kvittering"); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  const doMarkReady = async () => {
    setBusy(true);
    try {
      await http.post(`/v1/admin/repair-orders/${orderId}/mark-ready`, readyForm);
      toast.success("Merket klar. Kunde varslet (simulert i preview).");
      setShowReady(false); onOpenChange(false); onSaved && onSaved();
    } catch { toast.error("Feil ved markering"); } finally { setBusy(false); }
  };
  const doMarkDelivered = async () => {
    if (!window.confirm("Merke som utlevert? Dette stopper alle hentepåminnelser.")) return;
    const note = window.prompt("Kort merknad (valgfritt):", "") || "";
    setBusy(true);
    try {
      await http.post(`/v1/admin/repair-orders/${orderId}/mark-delivered`, { note });
      toast.success("Merket som utlevert.");
      onOpenChange(false); onSaved && onSaved();
    } catch { toast.error("Feil ved markering"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="repair-card">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 text-white">
            <span className="text-base">{orderId ? "Карточка ремонта" : "Новая карточка ремонта"}</span>
            {order?.order_number && <span className="px-2 py-0.5 rounded bg-blue-900/40 text-blue-200 border border-blue-700/50 text-xs">{order.order_number}</span>}
            {order && <span className="px-2 py-0.5 rounded bg-slate-700 text-slate-200 border border-slate-600 text-xs">{STATUS_RU[order.status] || order.status}</span>}
            {order && <span className="px-2 py-0.5 rounded text-xs border bg-slate-700 text-slate-200 border-slate-600">{priorityLabel[order.priority] || "Обычный"}</span>}
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">Цифровая карта приёмки и ремонта — одна запись на весь процесс.</DialogDescription>
        </DialogHeader>

        {loading ? <p className="text-slate-400 py-8 text-center">Загрузка...</p> : (
          <div className="space-y-4">
            {order?.machine_code && (
              <div className="rounded-lg border border-indigo-700/40 bg-indigo-950/20 p-3" data-testid="machine-panel">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <h3 className="text-sm font-semibold text-indigo-200">Машина / Maskin</h3>
                  <button type="button" onClick={() => printQr(order.machine_code, `${extras.machine?.brand || ""} ${extras.machine?.model || ""}`.trim())} data-testid="machine-qr-btn" className="shrink-0 px-2 py-1 rounded bg-indigo-600/80 hover:bg-indigo-600 text-white text-xs">Печать QR-наклейки</button>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="px-2 py-0.5 rounded bg-indigo-900/50 text-indigo-100 border border-indigo-700/50 font-mono">{order.machine_code}</span>
                  <span className="text-slate-200">{[extras.machine?.brand, extras.machine?.model].filter(Boolean).join(" ") || "—"}</span>
                  {extras.machine?.serial_number && <span className="text-slate-400">SN: {extras.machine.serial_number}</span>}
                </div>
                {extras.machine_history?.length > 0 && (
                  <div className="mt-2 border-t border-indigo-800/40 pt-2">
                    <p className="text-xs text-slate-400 mb-1">История этой машины ({extras.machine_history.length}):</p>
                    <ul className="space-y-1" data-testid="machine-history">
                      {extras.machine_history.map((h) => (
                        <li key={h.id} className="text-xs text-slate-300 flex flex-wrap gap-2">
                          <span className="font-mono text-indigo-200">{h.order_number}</span>
                          <span className="text-slate-400">{STATUS_RU[h.status] || h.status}</span>
                          <span className="text-slate-500 truncate max-w-[220px]">{h.problem_description}</span>
                          <span className="text-slate-600">{fmt(h.created_at)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {order && (
              <div className="rounded-lg border border-amber-700/40 bg-amber-950/20 p-3" data-testid="intake-summary">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <h3 className="text-sm font-semibold text-amber-200">Mottakssammendrag / Сводка приёмки</h3>
                  <button type="button" onClick={printReceipt} data-testid="print-receipt-btn" className="shrink-0 px-2 py-1 rounded bg-amber-600/80 hover:bg-amber-600 text-white text-xs">Skriv ut kvittering</button>
                </div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  {summaryRows.map(([k, v]) => (
                    <div key={k} className="flex gap-2"><dt className="text-slate-400 shrink-0">{k}:</dt><dd className="text-slate-100 break-words min-w-0">{v}</dd></div>
                  ))}
                </dl>
                {order.needs_manual_followup && (
                  <div className="mt-3 rounded border border-red-700/50 bg-red-950/30 p-2 text-xs text-red-200" data-testid="followup-banner">
                    Ikke hentet på over 60 dager – krever manuell oppfølging. Ingen automatisk kassasjon/salg skjer; enhver videre handling må vurderes og bekreftes manuelt i tråd med lovverket.
                  </div>
                )}
                {orderId && order.status !== "utlevert" && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {!showReady && <button type="button" onClick={() => setShowReady(true)} data-testid="btn-mark-ready" className="px-2 py-1 rounded bg-green-700/80 hover:bg-green-700 text-white text-xs">Merk klar &amp; varsle</button>}
                    <button type="button" onClick={doMarkDelivered} disabled={busy} data-testid="btn-mark-delivered" className="px-2 py-1 rounded bg-emerald-700/80 hover:bg-emerald-700 text-white text-xs disabled:opacity-50">Merk som utlevert</button>
                  </div>
                )}
                {orderId && order.status === "utlevert" && (
                  <p className="mt-3 text-xs text-emerald-300">Utlevert {fmtDate(order.delivered_at)}{order.delivered_by ? ` · ${order.delivered_by}` : ""}. Påminnelser stoppet.</p>
                )}
                {showReady && (
                  <div className="mt-3 rounded border border-green-700/40 bg-green-950/20 p-3 space-y-2" data-testid="mark-ready-panel">
                    <p className="text-xs font-semibold text-green-200">Bekreft: marker klar og varsle kunde</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <label className="text-xs text-slate-300">Type arbeid
                        <select value={readyForm.work_type} onChange={(e) => setReadyForm((f) => ({ ...f, work_type: e.target.value }))} className={`${inpCls} w-full rounded px-2 py-1.5 text-sm border mt-1`} data-testid="ready-work-type">
                          <option value="vedlikehold">Vedlikehold / service</option>
                          <option value="reparasjon">Reparasjon</option>
                          <option value="diagnose">Diagnose / feilsøking</option>
                          <option value="annet">Annet</option>
                        </select>
                      </label>
                      <label className="text-xs text-slate-300">Beløp (valgfritt)
                        <Input value={readyForm.final_amount} onChange={(e) => setReadyForm((f) => ({ ...f, final_amount: e.target.value }))} className={`${inpCls} mt-1`} placeholder="f.eks. 2 500 kr" data-testid="ready-amount" />
                      </label>
                    </div>
                    <label className="text-xs text-slate-300 block">Kort beskrivelse av utført arbeid
                      <Textarea value={readyForm.work_summary} onChange={(e) => setReadyForm((f) => ({ ...f, work_summary: e.target.value }))} className={`${inpCls} mt-1`} data-testid="ready-summary" />
                    </label>
                    <div className="flex gap-2">
                      <button type="button" onClick={doMarkReady} disabled={busy} data-testid="ready-confirm" className="px-2 py-1 rounded bg-green-700 hover:bg-green-600 text-white text-xs disabled:opacity-50">Merk klar og varsle kunde</button>
                      <button type="button" onClick={() => setShowReady(false)} className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-white text-xs">Avbryt</button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <Section title="Клиент">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Имя / компания *"><Input data-testid="card-customer-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className={inpCls} /></Field>
                <Field label="Телефон"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inpCls} />
                  <div className="mt-1.5"><SmsButton phone={form.phone} name={form.customer_name} jobCode={form.order_number} /></div></Field>
                <Field label="E-mail"><Input value={form.email} onChange={(e) => set("email", e.target.value)} className={inpCls} />
                  {orderId && <Link to={`/admin/kommunikasjon?order=${orderId}`} data-testid="card-reply-customer" className="mt-1.5 inline-block px-2.5 py-1 rounded bg-blue-700 hover:bg-blue-600 text-white text-xs">Ответить клиенту</Link>}</Field>
                <Field label="Тип клиента">
                  <select value={form.is_business ? "1" : "0"} onChange={(e) => set("is_business", e.target.value === "1")} className={`${inpCls} w-full rounded px-2 py-2 text-sm border`} data-testid="card-is-business">
                    <option value="0">Частный</option><option value="1">Бизнес</option>
                  </select>
                </Field>
              </div>
            </Section>

            <Section title="Машина">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Тип машины"><Input value={form.machine_type} onChange={(e) => set("machine_type", e.target.value)} className={inpCls} placeholder="кофемашина / швейная / ..." /></Field>
                <Field label="Марка *"><Input data-testid="card-machine-brand" value={form.machine_brand} onChange={(e) => set("machine_brand", e.target.value)} className={inpCls} /></Field>
                <Field label="Модель"><Input value={form.machine_model} onChange={(e) => set("machine_model", e.target.value)} className={inpCls} /></Field>
                <Field label="Серийный номер"><Input data-testid="card-serial" value={form.serial_number} onChange={(e) => set("serial_number", e.target.value)} className={inpCls} /></Field>
                <Field label="Местоположение / адрес клиента"><Input value={form.machine_location} onChange={(e) => set("machine_location", e.target.value)} className={inpCls} /></Field>
              </div>
            </Section>

            <Section title="Заявленная проблема">
              <Field label="Описание проблемы *"><Textarea data-testid="card-problem" value={form.problem_description} onChange={(e) => set("problem_description", e.target.value)} className={inpCls} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Когда началось"><Input value={form.when_occurs} onChange={(e) => set("when_occurs", e.target.value)} className={inpCls} /></Field>
                <Field label="Симптомы"><Input value={form.symptoms} onChange={(e) => set("symptoms", e.target.value)} className={inpCls} /></Field>
              </div>
              <Field label="Предыдущий ремонт / сервис (если есть)"><Input value={form.previous_service} onChange={(e) => set("previous_service", e.target.value)} className={inpCls} /></Field>
            </Section>

            <Section title="Прибыло с машиной (Medfølgende utstyr)">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {ACCESSORIES.map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                    <input type="checkbox" checked={!!form.accessories[k]} onChange={(e) => setNested("accessories", k, e.target.checked)} className="accent-blue-500" data-testid={`card-acc-${k}`} />
                    {label}
                  </label>
                ))}
              </div>
              <Field label="Другое / заметки"><Input value={form.accessories_notes} onChange={(e) => set("accessories_notes", e.target.value)} className={inpCls} /></Field>
            </Section>

            <Section title="Состояние при приёмке">
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input type="checkbox" checked={!!form.intake_condition.visible_damage} onChange={(e) => setNested("intake_condition", "visible_damage", e.target.checked)} className="accent-blue-500" data-testid="card-damage" />
                Видимые повреждения
              </label>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Заметки о внешнем состоянии"><Input value={form.intake_condition.condition_notes} onChange={(e) => setNested("intake_condition", "condition_notes", e.target.value)} className={inpCls} /></Field>
                <Field label="Отсутствующие части"><Input value={form.intake_condition.missing_parts} onChange={(e) => setNested("intake_condition", "missing_parts", e.target.value)} className={inpCls} /></Field>
                <Field label="Чистота">
                  <select value={form.intake_condition.cleanliness} onChange={(e) => setNested("intake_condition", "cleanliness", e.target.value)} className={`${inpCls} w-full rounded px-2 py-2 text-sm border`}>
                    {cleanlinessOpts.map((c) => <option key={c} value={c}>{c || "—"}</option>)}
                  </select>
                </Field>
                <Field label="Прочие наблюдения"><Input value={form.intake_condition.observations} onChange={(e) => setNested("intake_condition", "observations", e.target.value)} className={inpCls} /></Field>
              </div>
            </Section>

            <Section title="Фото / видео">
              {orderId ? <AdminRepairPhotos orderId={orderId} />
                : <p className="text-sm text-slate-500">Сохраните карточку, чтобы добавить фото к заказу. (Видео — в следующей фазе.)</p>}
            </Section>

            <Section title="Диагностика (техник)">
              <Field label="Диагноз"><Textarea value={form.diagnosis.text} onChange={(e) => setNested("diagnosis", "text", e.target.value)} className={inpCls} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Обнаружено"><Input value={form.diagnosis.findings} onChange={(e) => setNested("diagnosis", "findings", e.target.value)} className={inpCls} /></Field>
                <Field label="Рекомендованный ремонт"><Input value={form.diagnosis.recommended_repair} onChange={(e) => setNested("diagnosis", "recommended_repair", e.target.value)} className={inpCls} /></Field>
                <Field label="Необходимые запчасти"><Input value={form.diagnosis.parts_required} onChange={(e) => setNested("diagnosis", "parts_required", e.target.value)} className={inpCls} /></Field>
                <Field label="Оценка работ"><Input value={form.diagnosis.labour_estimate} onChange={(e) => setNested("diagnosis", "labour_estimate", e.target.value)} className={inpCls} /></Field>
              </div>
            </Section>

            <Section title="Предложение по ремонту">
              <p className="text-xs text-amber-300/80">Базовая цена: 1600 NOK. Всё, что превышает эту сумму, требует согласования с клиентом.</p>
              <Field label="Резюме диагноза"><Textarea value={form.proposal.diagnosis_summary} onChange={(e) => setNested("proposal", "diagnosis_summary", e.target.value)} className={inpCls} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Рекомендуемые работы"><Input value={form.proposal.recommended_work} onChange={(e) => setNested("proposal", "recommended_work", e.target.value)} className={inpCls} /></Field>
                <Field label="Необходимые детали"><Input value={form.proposal.required_parts} onChange={(e) => setNested("proposal", "required_parts", e.target.value)} className={inpCls} /></Field>
                <Field label="Оценка труда"><Input value={form.proposal.labour_estimate} onChange={(e) => setNested("proposal", "labour_estimate", e.target.value)} className={inpCls} /></Field>
                <Field label="Ориентировочная цена"><Input value={form.proposal.estimated_price} onChange={(e) => setNested("proposal", "estimated_price", e.target.value)} className={inpCls} /></Field>
                <Field label="Диапазон цены"><Input value={form.proposal.price_range} onChange={(e) => setNested("proposal", "price_range", e.target.value)} className={inpCls} /></Field>
                <Field label="НДС / MVA"><Input value={form.proposal.vat} onChange={(e) => setNested("proposal", "vat", e.target.value)} className={inpCls} /></Field>
              </div>
              <Field label="Условия"><Textarea value={form.proposal.conditions} onChange={(e) => setNested("proposal", "conditions", e.target.value)} className={inpCls} /></Field>
            </Section>

            <Section title="Одобрение клиента">
              {latestApproval ? (
                <div className="text-sm text-slate-300 space-y-1">
                  <p>Статус: <span className="font-semibold">{respLabel[latestApproval.response_type] || latestApproval.response_type}</span></p>
                  {latestApproval.responded_at && <p className="text-slate-400">Дата ответа: {fmt(latestApproval.responded_at)}</p>}
                  {latestApproval.comment && <p className="text-slate-400">Комментарий клиента: {latestApproval.comment}</p>}
                </div>
              ) : <p className="text-sm text-slate-500">Запрос на одобрение создаётся при статусе «Ожидает одобрения клиента» (отправляется по e-mail, ссылка /godkjenning).</p>}
            </Section>

            <Section title="Статус и приёмка">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Статус">
                  <select value={form.status} onChange={(e) => set("status", e.target.value)} className={`${inpCls} w-full rounded px-2 py-2 text-sm border`} data-testid="card-status">
                    {STATUS_KEYS.map((s) => <option key={s} value={s}>{STATUS_RU[s]}</option>)}
                  </select>
                </Field>
                <Field label="Приоритет">
                  <select value={form.priority} onChange={(e) => set("priority", e.target.value)} className={`${inpCls} w-full rounded px-2 py-2 text-sm border`} data-testid="card-priority">
                    {priorities.map((p) => <option key={p} value={p}>{priorityLabel[p]}</option>)}
                  </select>
                </Field>
                <Field label="Кто принял (приёмка)"><Input data-testid="card-received-by" value={form.received_by} onChange={(e) => set("received_by", e.target.value)} className={inpCls} /></Field>
                <Field label="Помогал при приёме/выдаче (необязательно)"><Input value={form.handover_assist} onChange={(e) => set("handover_assist", e.target.value)} className={inpCls} /></Field>
                <Field label="Стоимость (итог)"><Input value={form.estimated_cost} onChange={(e) => set("estimated_cost", e.target.value)} className={inpCls} /></Field>
              </div>
              {order && (() => {
                const sla = computeSla(order);
                if (!sla) return null;
                return (
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-400" data-testid="card-sla">
                    <span className={`px-2 py-0.5 rounded-full border ${slaBadgeClass[sla.state]}`}>SLA: {sla.label}</span>
                    <span>Внутренняя цель: {sla.hours}ч</span>
                    <span>· Принято: {fmt(order.received_at || order.created_at)}</span>
                    <span>· Первый ответ: {order.first_response_at ? fmt(order.first_response_at) : "ожидается"}</span>
                  </div>
                );
              })()}
            </Section>

            {orderId && extras.timeline.length > 0 && (
              <Section title="История статусов">
                <div className="space-y-2">
                  {extras.timeline.map((t) => (
                    <div key={t.id} className="text-sm text-slate-300 flex flex-wrap gap-2 items-center">
                      <span className="px-2 py-0.5 rounded bg-slate-700 border border-slate-600 text-xs">{STATUS_RU[t.new_status] || t.new_status}</span>
                      <span className="text-slate-500 text-xs">{fmt(t.created_at)}</span>
                      {t.changed_by && <span className="text-slate-500 text-xs">· {t.changed_by}</span>}
                      {t.note && <span className="text-slate-400 text-xs">— {t.note}</span>}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {orderId && extras.messages.length > 0 && (
              <Section title="Сообщения клиенту">
                <div className="space-y-2">
                  {extras.messages.map((m) => (
                    <div key={m.id} className="text-sm bg-slate-900/40 border border-slate-700 rounded p-2">
                      <p className="text-slate-500 text-xs mb-1">{m.category} · {fmt(m.created_at)}</p>
                      <p className="text-slate-200 whitespace-pre-wrap">{m.text}</p>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {orderId && (
              <Section title="Økonomi (faktura · betaling · innkjøp)">
                <AdminBillingSection orderId={orderId} initialBilling={order?.billing} />
              </Section>
            )}

            <Section title="Внутренние заметки (не видны клиенту)">
              <Textarea value={form.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} className={inpCls} />
            </Section>
          </div>
        )}

        <DialogFooter>
          <label className="flex items-center gap-2 text-sm text-slate-300 mr-auto" title="Vadim koostaa ja saadab sõnumi kliendi keeles — ainult staatuse muutmisel">
            <input type="checkbox" checked={notifyCustomer} onChange={(e) => setNotifyCustomer(e.target.checked)} data-testid="card-notify-customer" />
            Уведомить клиента (Vadim напишет на его языке)
          </label>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save} data-testid="card-save">Сохранить карточку</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
